// Rails rate-limits POST endpoints via Rack::Attack by returning a
// plain-text "Retry later" body (typical throttled_response). RTK
// Query's base query then fails to JSON-parse it and surfaces
// `{ status: 'PARSING_ERROR', error: 'SyntaxError…', originalStatus: 429 }`.
// Also match a real HTTP 429 in case the API stops sending the
// plaintext body. Bulk flows (create-board × N + create-card × N +
// create-schedule × N in tight sequence) trip this reliably.
export function isRateLimited(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as {
    status?: number | string;
    originalStatus?: number;
  };
  // Only two cases: a real 429, OR a PARSING_ERROR whose underlying
  // originalStatus is 429 (Rack::Attack's plaintext "Retry later"
  // body). A PARSING_ERROR with originalStatus 200/500/etc. means the
  // server returned malformed/HTML content on a real response — the
  // request likely succeeded server-side, so retrying it would create
  // duplicate boards/cards/groups. Fail through instead.
  if (e.status === 429) return true;
  if (e.status === 'PARSING_ERROR' && e.originalStatus === 429) return true;
  return false;
}

// Bounds for a server-supplied Retry-After hint. Below 100ms is
// almost certainly a bug (rounding to 0s, misconfigured header) and
// would defeat the point of backing off; above 30s a curator-facing
// bulk flow shouldn't quietly stall — surface the failure to them
// instead of pretending to be busy for half a minute.
const RETRY_AFTER_MIN_MS = 100;
export const RETRY_AFTER_MAX_MS = 30_000;

// Populated by the base-query fetch interceptor (src/store/api/api.ts)
// on every 429 response, consumed-and-cleared by the next backoff.
// createAll runs its mutations serially so the singleton is a fine
// fit; if this ever moves to a parallel flow, key by request instead.
let lastRetryAfterMs: number | null = null;

// Parse an HTTP `Retry-After` header. Two shapes per RFC 9110:
//   - integer seconds (Rack::Attack, Cloudflare, most servers): "5"
//   - HTTP-date (rare in APIs): "Wed, 21 Oct 2015 07:28:00 GMT"
// Returns ms, clamped to [MIN, MAX]; null on unparseable input so
// the caller falls back to the hardcoded exponential ladder.
export function parseRetryAfter(header: string | null): number | null {
  const ms = parseRetryAfterUncapped(header);
  return ms === null ? null : Math.min(ms, RETRY_AFTER_MAX_MS);
}

// The delay the server asked for, with the floor applied but no ceiling.
// A caller that is not willing to wait that long should stop retrying rather
// than replay early: coming back before the window resets is a guaranteed
// second 429, which spends an attempt to learn nothing. `parseRetryAfter`
// keeps the ceiling for `withRateLimitRetry`, whose ladder bounds the wait
// on its own.
export function parseRetryAfterUncapped(header: string | null): number | null {
  if (!header) return null;
  const trimmed = header.trim();
  if (!trimmed) return null;

  const asInt = Number(trimmed);
  if (Number.isFinite(asInt) && asInt >= 0) {
    return floorRetryAfter(asInt * 1000);
  }

  const asDate = Date.parse(trimmed);
  if (Number.isFinite(asDate)) {
    return floorRetryAfter(asDate - Date.now());
  }

  return null;
}

function floorRetryAfter(ms: number): number | null {
  if (!Number.isFinite(ms)) return null;
  // 0 or negative is a floor case, not a reject case — a server-sent
  // "0" or a past HTTP-date means "the window just reset", and we
  // still want a token pause before hammering the endpoint again.
  return ms < RETRY_AFTER_MIN_MS ? RETRY_AFTER_MIN_MS : ms;
}

// Called by the base-query fetchFn on every 429. Stores the parsed
// value for the next `withRateLimitRetry` invocation to pick up.
export function recordRetryAfter(header: string | null): void {
  const parsed = parseRetryAfter(header);
  if (parsed !== null) lastRetryAfterMs = parsed;
}

// Consumed by the retry helper on backoff — reads the last stored
// value and clears the slot so a stale hint from a prior flow can't
// leak into an unrelated future retry.
function takeRetryAfterHint(): number | null {
  const v = lastRetryAfterMs;
  lastRetryAfterMs = null;
  return v;
}

const DEFAULT_DELAYS_MS = [500, 1500, 4000];
const sleep = (ms: number): Promise<void> =>
  new Promise((r) => setTimeout(r, ms));

// Run `op` and, on a rate-limit response, retry with exponential
// backoff. Non-rate-limit errors bubble immediately so the row-level
// catch upstream still surfaces them. When the server returned a
// `Retry-After` header on the 429, that value wins over the current
// step in the hardcoded ladder — using the server's hint is always
// more accurate than our guess. Ladder still bounds total attempts.
export async function withRateLimitRetry<T>(
  op: () => Promise<T>,
  delaysMs: readonly number[] = DEFAULT_DELAYS_MS,
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= delaysMs.length; attempt++) {
    try {
      return await op();
    } catch (err) {
      lastErr = err;
      if (!isRateLimited(err)) throw err;
      if (attempt === delaysMs.length) break;
      const serverHint = takeRetryAfterHint();
      const delay = serverHint ?? delaysMs[attempt]!;
      await sleep(delay);
    }
  }
  throw lastErr;
}
